import '../styles/global.scss';
import classNames from 'classnames';
import Site from 'components/unlisted/Site';
import GridChecker from 'components/utils/grid-checker/GridChecker';
import { enableStaticRendering } from 'mobx-react-lite';
import type { AppContext, AppInitialProps, AppProps } from 'next/app';
import App from 'next/app';
import { GlobalStoreProvider } from '@/stores/global';
import type { StoryblokResult } from 'storyblok-js-client';
import { FlowProvider } from 'util/react-transition-component/components/FlowProvider';
import { FlowRoutePresence } from 'util/react-transition-component/components/FlowRoutePresence';
import { useDeviceStateTracker } from '../hooks/useDeviceStateTracker';
import { loadAsyncPolyfill } from '../util/polyfill';
import useViewportCustomProperties from '../util/setViewportCustomProperties';
import { fetchStory, getLocationsList, getSpace } from '../util/storyblok/api';
import { globalSettingsSlug, StorySlug } from '../util/storyblok/types/api.types';
import type { GlobalSettings } from '../util/storyblok/types/global.types';
import styles from './_app.module.scss';
import { updateCacheVersion } from '../util/storyblok/api.utils';
import type { LocationBlokData } from '../components/blocks/location/Location.data';
import { getHostName, stripQueriesFromUrl } from '../util/url.utils';
import { CanPlayWebmContextProvider } from '../context/canPlayWebmContext';

import type { JSX } from 'react';

enableStaticRendering(typeof window === 'undefined');
loadAsyncPolyfill();

function MyApp({
  Component,
  pageProps,
  router,
  globalSettings,
  locationsList,
  domain,
}: AppProps & {
  globalSettings: GlobalSettings;
  locationsList?: Array<LocationBlokData>;
  domain?: string;
}): JSX.Element {
  const isCasePage = router.asPath.includes(`/${StorySlug.Cases}/`);
  useDeviceStateTracker();
  useViewportCustomProperties();

  const path = stripQueriesFromUrl(router.asPath);

  return (
    <GlobalStoreProvider>
      <FlowProvider>
        <CanPlayWebmContextProvider>
          <noscript
            dangerouslySetInnerHTML={{
              __html: `
            <iframe 
              src='https://www.googletagmanager.com/ns.html?id=GTM-MBDGVBP'
              height='0' width='0' style='display:none; visibility:hidden'
            />
          `,
            }}
          />
          <noscript
            dangerouslySetInnerHTML={{
              __html: `
              <img height="1" width="1" style="display:none;" alt="" src="https://px.ads.linkedin.com/collect/?pid=5270034&fmt=gif" />
          `,
            }}
          />
          {/* Under construction boolean from global settings, if enabled, hides entire site. */}
          {globalSettings.construction ? (
            <div className={classNames(styles.logo)}>
              <svg viewBox="0 0 159 102" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M25.2426 0H0V21.6109H25.298C41.2841 21.6109 54.2901 34.6883 54.2901 50.7621C54.2901 66.8358 41.2841 79.9132 25.298 79.9132H0V101.524H25.2426C53.0805 101.524 75.7277 78.7527 75.7277 50.7621C75.7277 22.7715 53.0805 0 25.2426 0Z" />
                <path d="M133.706 79.1133C118.171 79.1133 105.51 66.3966 105.51 50.7653C105.51 35.1341 118.157 22.4141 133.706 22.4141H159.001V0H133.759C105.921 0 83.2734 22.7715 83.2734 50.7621C83.2734 78.7527 105.921 101.524 133.759 101.524H159.001V79.11H133.706V79.1133Z" />
              </svg>
            </div>
          ) : (
            <Site
              locationsList={locationsList}
              globalSettings={globalSettings}
              domain={domain}
              seoTitle={pageProps?.story?.content?.seo_title || pageProps?.story?.name}
              seoDescription={
                pageProps?.story?.content?.seo_description &&
                pageProps?.story?.content?.seo_description.length > 0
                  ? pageProps?.story?.content?.seo_description
                  : undefined
              }
              noFollow={pageProps?.story?.content.no_follow}
              seoImage={
                pageProps?.story?.content?.key_image?.[0]?.image_landscape ||
                pageProps?.story?.content?.seo_image?.filename
              }
              seoVideo={pageProps?.story?.content?.seo_video?.filename}
              seoImagePortrait={pageProps?.story?.content?.seo_image_portrait?.filename}
              excludeFooter={isCasePage}
            >
              <FlowRoutePresence>
                <Component
                  key={`${router.locale}:${path}`}
                  {...pageProps}
                  className={classNames(styles.page)}
                />
              </FlowRoutePresence>
              {process.env.NODE_ENV === 'development' && <GridChecker />}
            </Site>
          )}
        </CanPlayWebmContextProvider>
      </FlowProvider>
    </GlobalStoreProvider>
  );
}

export default MyApp;

/**
 *  @MyApp.getInitialProps
 *  Only use this method if you have blocking data requirements for
 *  every single page in your application. This disables the ability to
 *  perform automatic static optimization, causing every page in your app to
 *  be server-side rendered.
 *
 *  https://nextjs.org/docs/advanced-features/custom-app#caveats
 *  Adding a custom getInitialProps in your App will disable Automatic Static Optimization in pages without Static Generation.
 */
MyApp.getInitialProps = async (
  appContext: AppContext,
): Promise<
  AppInitialProps & {
    globalSettings: GlobalSettings;
    locationsList?: Array<LocationBlokData>;
    domain?: string;
  }
> => {
  const { locale } = appContext.router;
  const host = getHostName(appContext?.ctx?.req);
  const appProps = await App.getInitialProps(appContext);

  let spaceResponse;
  try {
    // todo: write a cache version to a file from the webhook instead, once webhook is being used.
    spaceResponse = await getSpace(appContext.router.isPreview);
    const { version } = spaceResponse.data.space;
    updateCacheVersion(version);
  } catch (error) {
    throw new Error(`Failed to fetch space`);
  }

  let locationsList!: Array<LocationBlokData> | undefined;
  if (
    appContext.router.asPath.includes(`/${StorySlug.Locations}`) ||
    appContext.router.asPath.includes(`/${StorySlug.Location}/`)
  ) {
    try {
      locationsList = await getLocationsList(
        {
          language: locale,
        },
        appContext.router.isPreview,
      );
    } catch (error) {
      throw new Error(`Failed to fetch ${StorySlug.Locations}`);
    }
  }

  let response!: StoryblokResult;
  try {
    response = await fetchStory(
      globalSettingsSlug,
      {
        language: locale,
      },
      appContext.router.isPreview,
    );
  } catch (error) {
    throw new Error(`Failed to fetch ${globalSettingsSlug}`);
  }
  const globalSettings = response.data.story.content;

  return {
    ...appProps,
    globalSettings,
    locationsList,
    domain: host,
  };
};
